import { useHomeQuery } from '@/queries/home.query';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

const BannerSkeleton = () => (
  <div className="relative w-full h-[80vh] lg:h-[800px] bg-gradient-to-r from-gray-600 via-gray-500 to-gray-600 animate-pulse" />
);

const BannerImage = ({ desktop, mobile, alt, priority }) => (
  <picture>
    <source media="(max-width: 768px)" srcSet={mobile} />
    <source media="(min-width: 769px)" srcSet={desktop} />
    <img
      src={desktop}
      alt={alt}
      width="1920"
      height="800"
      draggable={false}
      onContextMenu={(e) => e.preventDefault()}
      className="w-full h-full object-cover"
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      {...(priority && { fetchpriority: 'high' })}
    />
  </picture>
);

const ParallaxSlider = () => {
  const sliderRef = useRef(null);
  const intervalRef = useRef(null);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState([]);

  /* ========================
   * Query Home
   * ======================== */
  const { data, isLoading, isError } = useHomeQuery();

  /* ========================
   * Mapping Banner
   * ======================== */
  const images = useMemo(() => {
    if (!data?.data?.banner) return [];

    return [...data.data.banner]
      .sort((a, b) => a.position - b.position)
      .map((item) => ({
        desktop: item.banner_desktop,
        mobile: item.banner_mobile,
      }));
  }, [data]);

  /* ========================
   * Init Progress
   * ======================== */
  useEffect(() => {
    if (images.length) {
      setProgress(new Array(images.length).fill(0));
    }
  }, [images.length]);

  /* ========================
   * Auto Progress
   * ======================== */
  useEffect(() => {
    if (!images.length) return;

    clearInterval(intervalRef.current);
    let value = 0;

    intervalRef.current = setInterval(() => {
      value += 1;

      setProgress((prev) => {
        const updated = [...prev];
        updated[currentSlide] = value;
        return updated;
      });

      if (value >= 100) {
        clearInterval(intervalRef.current);
        sliderRef.current?.slickNext();
      }
    }, 50);

    return () => clearInterval(intervalRef.current);
  }, [currentSlide, images.length]);

  const settings = {
    dots: false,
    fade: true,
    infinite: true,
    speed: 1500,
    autoplay: false,
    arrows: false,
    beforeChange: (_, next) => {
      setProgress((prev) => prev.map((_, i) => (i === next ? 0 : prev[i])));
      setCurrentSlide(next);
    },
  };

  if (isLoading) return <BannerSkeleton />;
  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-primary/80 text-white text-lg">
        Oops, Terjadi kesalahan
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <Slider ref={sliderRef} {...settings}>
        {images.map((img, index) => (
          <div key={index} className="relative">
            <BannerImage
              desktop={img.desktop}
              mobile={img.mobile}
              alt={`Banner ${index + 1}`}
              priority={index === 0}
            />

            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, i) => (
                <div
                  key={i}
                  className={`relative overflow-hidden ${
                    currentSlide === i
                      ? 'w-40 h-3 rounded-full bg-black/10'
                      : 'w-3 h-3 rounded-full bg-black/10'
                  }`}
                >
                  {currentSlide === i && (
                    <div
                      className="absolute inset-y-0 left-0 bg-primary/80 rounded-full"
                      style={{ width: `${progress[i]}%` }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </Slider>

      <button
        onClick={() => sliderRef.current?.slickPrev()}
        className="absolute top-1/2 left-4 -translate-y-1/2 bg-black/40 p-2 rounded-full text-white"
      >
        <ArrowLeftIcon className="w-7 h-7" />
      </button>

      <button
        onClick={() => sliderRef.current?.slickNext()}
        className="absolute top-1/2 right-4 -translate-y-1/2 bg-black/40 p-2 rounded-full text-white"
      >
        <ArrowRightIcon className="w-7 h-7" />
      </button>
    </div>
  );
};

export default ParallaxSlider;
